import { z } from 'zod';
/**
 * Der Vertrag der Power-Suche — `GET /api/v1/suche`.
 *
 * **Ein Feld, sechs Bestände, Treffer nach Belegart gruppiert.** Was hier
 * beschrieben wird, ist ein **Sprungbrett** und keine Tabelle: Je Gruppe stehen
 * die ersten Treffer und darunter, wie viele es insgesamt sind.
 *
 * **Die Summe ist eine Anzeige und niemals ein Bericht.** Sie läuft über die
 * vollständige Treffermenge, **je Währung getrennt**, und wird nie über
 * Währungen hinweg addiert — es gibt keinen Kurs im System, und einen zu
 * erfinden wäre eine Bewertung. Sie erscheint in keinem Export.
 *
 * **Beträge sind Zeichenketten und niemals Zahlen** (Hausregel 1). Der Treiber
 * liefert `numeric` ohnehin als Zeichenkette; sie durch `number` zu schicken
 * wäre ein Rundungsfehler mit Anlauf.
 */
/**
 * Die sechs Belegarten — zugleich die **feste** Reihenfolge der Anzeige.
 *
 * **Fest und nicht «die grösste Gruppe zuerst».** Wer zweimal dasselbe sucht,
 * soll zweimal dieselbe Anordnung vorfinden; eine Liste, die ihre Reihenfolge
 * nach dem Ergebnis wechselt, kostet bei jedem Blick eine neue Orientierung.
 *
 * **Diese Liste ist die Quelle der Wahrheit**, auch für die Datenbank: Der
 * `CHECK` auf `such_index.art` in Migration `0067` führt dieselben sechs Werte.
 * Eine siebte Art kostet einen Speisungsweg und eine Migration mit einem
 * geänderten `CHECK` — und ausdrücklich kein `ALTER TYPE`, weil `art` ein Text
 * ist und kein Aufzählungstyp.
 */
export const SUCHARTEN = [
    'ausgangsbeleg',
    'eingangsrechnung',
    'bankumsatz',
    'buchung',
    'kreditor',
    'kunde',
];
/** Eine Belegart des Suchindex. */
export const suchartSchema = z.enum(SUCHARTEN);
/**
 * Die kleinste Zahl an Zeichen, mit der ein Freitextbegriff ausgeführt wird.
 *
 * **Drei, und das ist eine Grenze der Mechanik und kein Ausbaupunkt.** Ein
 * Trigramm-Index greift erst ab drei Zeichen; darunter fiele die Suche auf
 * einen sequenziellen Durchlauf zurück — gemessen 1,6 µs je Zeile, also 222 ms
 * über 140.900 Zeilen. Ein Begriff mit ein oder zwei Zeichen wird deshalb
 * **nicht ausgeführt, sondern erklärt.**
 */
export const FREITEXT_MINDESTLAENGE = 3;
/**
 * Die Rangklasse eines Treffers — die **Klasse schlägt die Punktzahl**.
 *
 * Sortiert wird erst nach Klasse, dann nach Punktzahl, dann nach Datum
 * absteigend. Eine Punktzahl kann eine Klasse nie überholen; das ist die
 * mechanische Fassung von «ein exakter Belegnummerntreffer gehört immer nach
 * oben».
 *
 * | Klasse | Was hineinfällt |
 * |---|---|
 * | `0` | Kennungsform des Begriffs **gleich** Belegnummer, IBAN oder Kontonummer |
 * | `1` | Kennungsform als Teilzeichenfolge; Anfang schlägt Mitte |
 * | `2` | Teilzeichenfolge auf der Suchform in Name, Text, Verwendungszweck |
 * | `3` | ungefähr (Trigramm-Ähnlichkeit) — **entsteht in Stufe 1 nicht** |
 *
 * Klasse `3` steht im Vertrag und wird von Stufe 1 nie erzeugt. Sie steht da,
 * weil der Vertrag sonst mit Stufe 5 bräche — und ein Klient, der heute mit
 * `0..2` rechnet, morgen an einem Wert scheiterte, den er nicht kennt.
 */
export const rangklasseSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
]);
/**
 * Ein Betrag, wie er über die Schnittstelle geht: eine Zeichenkette mit genau
 * zwei Nachkommastellen.
 *
 * Ausdrücklich **kein** `z.number()`. Das ist Hausregel 1, und sie gilt auch
 * für eine Suchmaske: Ein Betrag, der einmal durch ein `double` gegangen ist,
 * ist nicht mehr derselbe Betrag.
 */
const betragSchema = z.string().regex(/^-?\d+\.\d{2}$/, 'Kein gültiger Betrag');
/** Ein Datum im ISO-Format. */
const isoDatum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kein gültiges Datum');
/**
 * Eine Trefferzeile.
 *
 * **`kennung`, `partner` und `text` kommen in der ROHFASSUNG**, nicht in der
 * Suchform. Die Suchform ist der Vergleichsraum und nicht die Anzeige: Ein
 * Anwender, der `Müller GmbH` sucht und `mueller gmbh` hervorgehoben bekommt,
 * glaubt an einen Datenfehler. Die Hervorhebung setzt die Oberfläche auf dieser
 * Rohfassung.
 */
export const suchtrefferSchema = z
    .object({
    art: suchartSchema,
    /** Der Primärschlüssel in der Quelltabelle — der Weg zum Beleg. */
    id: z.string().uuid(),
    /** Belegnummer, Journalnummer, Kreditoren- oder Kundennummer. */
    kennung: z.string().nullable(),
    /** Der Partner, **wie auf dem Beleg festgehalten**. */
    partner: z.string().nullable(),
    /** Der zusammengesetzte Freitext, gekürzt. */
    text: z.string().nullable(),
    betrag: betragSchema.nullable(),
    waehrung: z.string().length(3).nullable(),
    datum: isoDatum.nullable(),
    status: z.string().nullable(),
    rangklasse: rangklasseSchema,
})
    .strict();
/**
 * Eine Gruppe: eine Belegart, ihre Trefferzahl und die ersten Treffer.
 *
 * **`anzahl` ist die vollständige Trefferzahl und nicht `treffer.length`.**
 * Genau darin liegt der Nutzen: «… und 23 weitere» ist der Weg in die
 * vollständige Liste der Art.
 *
 * **Eine Gruppe ohne Treffer erscheint nicht** — und eine durch die
 * Fähigkeitsgrenze ausgeschlossene Art erscheint ebenfalls nicht, und zwar ohne
 * Hinweis darauf, dass es sie gibt.
 */
export const suchgruppeSchema = z
    .object({
    art: suchartSchema,
    anzahl: z.number().int().nonnegative(),
    treffer: z.array(suchtrefferSchema),
})
    .strict();
/**
 * Die Summe einer Währung über die **vollständige** Treffermenge.
 *
 * Fünf Regeln, und jede verhindert eine falsche Zahl: über die ganze
 * Treffermenge und nicht über die angezeigten fünf; nie über Währungen hinweg
 * addiert; ohne die ungefähren Treffer; ohne Kreditoren und Kunden (ein
 * Stammsatz trägt keinen Betrag); und beschriftet als das, was sie ist.
 *
 * **Buchungen fehlen hier, und das ist die Datenlage und kein Versäumnis:** Das
 * Journal führt keine Währungsspalte. Eine Buchung erscheint mit ihrer
 * Sollsumme in der Trefferliste und in keiner Währungssumme.
 */
export const suchsummeSchema = z
    .object({
    waehrung: z.string().length(3),
    betrag: betragSchema,
    anzahl: z.number().int().nonnegative(),
})
    .strict();
/**
 * Ein Zähler der Zeile `STAND` — je Belegzustand und Belegart eine Zahl über
 * die **volle** Treffermenge (Spec 16.9.1).
 *
 * **`stand` trägt das deutsche Zustandswort und nicht den technischen Wert.**
 * `status:` nimmt beides an (`liesStatus`), aber nur das deutsche Wort ist das,
 * was ein Klick auf die Facette in die Eingabe schreiben darf: Ein Chip
 * `[posted 3]`, der `status:posted` einfügt, lehrte das Vokabular, das die
 * Sprache gerade vermeiden soll.
 *
 * **Zeilen mit `anzahl: 0` sind der Zweck und kein Abfall.** Sie sind die
 * grauen Nullen des Entwurfs (Spec 16.6.5): Eine Facette, die verschwindet,
 * sobald sie null zählt, lässt den Anwender raten, ob es den Zustand nicht gibt
 * oder ob er leer ist. Geliefert werden sie **nur für Belegarten, die der
 * Aufrufer sehen darf** — sonst verriete eine Null eine Art, die Spec 7.1
 * verschweigt.
 */
export const suchstandSchema = z
    .object({
    art: suchartSchema,
    /** Das deutsche Zustandswort — genau die Schreibweise, die `status:` annimmt. */
    stand: z.string(),
    anzahl: z.number().int().nonnegative(),
})
    .strict();
/**
 * «Ohne „betrag > 500,00 €" wären es 12 Treffer.» — die nützlichste Zeile des
 * leeren Zustands (Spec 7.4).
 *
 * **Sie entsteht nur bei null Treffern.** Dort kostet sie nichts, weil die
 * eigentliche Abfrage nichts gefunden hat; bei Treffern wäre sie je Element
 * eine zusätzliche Zählabfrage für eine Frage, die niemand gestellt hat.
 *
 * Sie beantwortet, was der Anwender sonst durch Ausprobieren beantwortet, und
 * trennt «vertippt» von «gibt es nicht» ein zweites Mal.
 */
export const ohnezeileSchema = z
    .object({
    /** Die Rohfassung des weggelassenen Elements, wörtlich aus der Eingabe. */
    roh: z.string(),
    /** Seine ausgeschriebene Fassung — `betrag > 500,00 €`. */
    text: z.string(),
    /** Wie viele Treffer die Anfrage **ohne** dieses Element hätte. */
    anzahl: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Schlüssel der Suchsprache (Stufe 2) — deutsch, sprechend, elf Stück.
 *
 * **Kein Schlüssel heisst wie eine Spalte**, und das ist Absicht: Der Anwender
 * spricht von einem Kreditor und nicht von `vendor_legal_name`. Die Liste ist
 * zugleich der Wertebereich des Vorschlagsendpunkts (Stufe 3) und der
 * Fehlermeldung «kein bekannter Filter».
 *
 * `vor` und `nach` sind Kurzformen von `datum<` und `datum>` und stehen
 * trotzdem als eigene Schlüssel da: Sie werden getippt, sie erscheinen in der
 * Vorschlagsliste, und ein Schlüssel, den man tippen kann, gehört in die Liste
 * der Schlüssel.
 */
export const SUCHSCHLUESSEL = [
    'kreditor',
    'kunde',
    'nummer',
    'betrag',
    'datum',
    'vor',
    'nach',
    'status',
    'art',
    'konto',
    'iban',
    'waehrung',
];
/** Ein Schlüssel der Suchsprache. */
export const suchschluesselSchema = z.enum(SUCHSCHLUESSEL);
/**
 * Die Kurzerklärung je Schlüssel — die rechte Spalte der Vorschlagsliste
 * (Spec 16.6.6).
 *
 * **Sie steht im Vertrag und nicht in der Oberfläche**, und der Grund ist
 * derselbe wie bei `SUCHSCHLUESSEL` selbst: Die Vorschlagsliste ist die einzige
 * Stelle, an der ein Anwender erfährt, dass es einen Schlüssel gibt. Stünde die
 * Liste der Schlüssel hier und die Liste ihrer Erklärungen in der Maske, hätte
 * ein neuer Schlüssel eine leere Zeile — sichtbar, aber unerklärt. Der Typ
 * `Record<Suchschluessel, string>` macht die Lücke stattdessen zu einem
 * Übersetzungsfehler.
 *
 * **`typ:` heisst hier `art:`.** Der Bildschirmentwurf zeigt `typ:`; die
 * gelandete Sprache führt `art:`, und einen zweiten Namen für denselben
 * Schlüssel gibt es nicht — er wäre die zweite Wahrheit, gegen die Spec 5.1
 * warnt.
 */
export const SCHLUESSEL_ERKLAERUNG = {
    kreditor: 'Geschäftspartner auf der Eingangsrechnung',
    kunde: 'Geschäftspartner auf dem Ausgangsbeleg',
    nummer: 'Belegnummer, auch teilweise',
    betrag: 'Bruttobetrag, mit > < vergleichbar',
    datum: 'Belegdatum, mit > < vergleichbar',
    vor: 'Belegdatum vor …',
    nach: 'Belegdatum nach …',
    status: 'Bearbeitungsstand',
    art: 'Belegart',
    konto: 'Sachkonto',
    iban: 'IBAN',
    waehrung: 'Währung des Belegs',
};
/**
 * Der Zustand eines Elements der Interpretation — **vier** und nicht drei.
 *
 * Spec 4.6 führt drei Zustände, und der dritte ist der, den man beim Entwerfen
 * vergisst:
 *
 * | Zustand | Beispiel | Verhalten |
 * |---|---|---|
 * | `gueltig` | `betrag>500` | fliesst in die Abfrage |
 * | `in-eingabe` | `betrag>`, `kreditor:` | **kein Fehler**; wird aus der Abfrage genommen und blass gezeichnet |
 * | `ungueltig` | `betrag>fünfhundert` | **die Abfrage läuft nicht** — `400` mit dieser Interpretation |
 *
 * **Der vierte ist keine Erfindung, sondern die Trennung zweier Dinge, die
 * beide «läuft nicht» heissen und Verschiedenes bedeuten.** `nicht-ausfuehrbar`
 * trägt den Freitextbegriff unter drei Zeichen (Spec 4.3): Er ist **verstanden**
 * und **richtig geschrieben** — der Trigramm-Index greift nur erst ab drei
 * Zeichen. Das ist eine Aussage über die Mechanik und nicht über die Eingabe,
 * und deshalb ist die Antwort darauf `200` mit leerer Trefferliste und
 * Erklärung (wie in Stufe 1) und nicht `400`. Wer beides `ungueltig` nennt,
 * schickt dem Anwender für einen richtig getippten Begriff eine Ablehnung.
 */
export const elementzustandSchema = z.enum([
    'gueltig',
    'in-eingabe',
    'ungueltig',
    'nicht-ausfuehrbar',
]);
/**
 * Was der Server aus der Anfrage gemacht hat.
 *
 * **Sie kommt auch im Fehlerfall zurück**, und das ist die wichtigste Zusage
 * dieses Vertrags: Ein `400` ohne Interpretation zwänge den Klienten, die
 * Grammatik selbst zu kennen — und dann entschiede er doch, was die Anfrage
 * bedeutet. Die Auswertung liegt auf dem Server. Immer.
 *
 * In Stufe 1 trägt sie ein einziges Element (den Freitextbegriff) oder gar
 * keines. Ab Stufe 2 stehen hier die Filter der Suchsprache.
 */
export const interpretationselementSchema = z
    .object({
    /** Was verstanden wurde, ausgeschrieben: `Freitext "müller"`. */
    text: z.string(),
    /** Gesetzt, wenn dieses Element nicht ausgeführt werden konnte. */
    hinweis: z.string().nullable(),
    /** Der Zustand des Elements — siehe {@link elementzustandSchema}. */
    zustand: elementzustandSchema,
    /**
     * Die **Rohfassung** dieses Elements, wörtlich aus der Eingabe.
     *
     * Sie ist der Anker der Oberfläche: Ein Token wird nicht aus `text`
     * gezeichnet, sondern aus dem Stück Eingabe, das an `stelle` steht. **Der
     * Text bleibt die Wahrheit** (Spec 5.1); `roh` sagt nur, welcher Teil davon
     * zu diesem Element gehört.
     */
    roh: z.string(),
    /** Der Versatz von `roh` in der Anfrage — die Stelle für den Fehlerzeiger. */
    stelle: z.number().int().nonnegative(),
    /** Der Schlüssel, wenn dieses Element ein Filter ist. */
    schluessel: suchschluesselSchema.nullable(),
    /**
     * Was erwartet wurde — nur bei `ungueltig`.
     *
     * `kein Betrag — erwartet z. B. 500 oder 1.234,56`. Ohne diesen Satz
     * bekommt der Anwender eine Ablehnung ohne Ausweg.
     */
    erwartet: z.string().nullable(),
    /**
     * «Meinten Sie …?» — nur, wo es einen **verlässlichen** Rat gibt.
     *
     * Er kommt aus der **Werteliste des Schlüssels** und nie aus dem Bestand:
     * Bei `status:` gibt es eine überschaubare Zahl möglicher Werte, bei
     * `kreditor:` gibt es keinen solchen Rat — dort ist ein unbekannter Name
     * **kein Fehler**, sondern eine leere Trefferliste (Spec 4.6).
     */
    vorschlag: z.string().nullable(),
})
    .strict();
/**
 * Die Antwort auf eine Suche.
 *
 * `folge` ist die Folgenummer aus der Anfrage, unverändert zurückgegeben. Der
 * Klient verwirft jede Antwort mit kleinerer Nummer als die zuletzt angezeigte
 * — **auch wenn sie ankommt**. Der Abbruch über `AbortController` ist ein
 * Wunsch, kein Vertrag: Eine Antwort kann bereits unterwegs sein, wenn er
 * ausgesprochen wird. Die Folgenummer ist die Zusage.
 */
export const suchergebnisSchema = z
    .object({
    interpretation: z.array(interpretationselementSchema),
    /**
     * Der verstandene Ausdruck, **voll ausgeschriebene Klammerung** inbegriffen.
     *
     * `( kreditor = "mueller" ODER kreditor = "schulze" ) UND betrag > 500,00 €`
     * — auch die Klammern, die niemand getippt hat. Das ist der Preis und der
     * Nutzen des impliziten UND (Spec 3.1, 4.5): `a ODER b c` heisst
     * `a ODER (b UND c)`, und genau dort verlesen sich Menschen.
     *
     * In Stufe 1 stand hier der Freitextbegriff allein; leer ist er nur bei
     * leerer Anfrage.
     */
    ausdruck: z.string(),
    /**
     * Die Belegarten, in denen gesucht wird — die Zeile `→ sucht in:`.
     *
     * **Ein Schlüssel, der auf eine Belegart nicht wirkt, schliesst diese Art
     * aus** (Spec 4.2). `kreditor:müller` liefert keine Ausgangsbelege — nicht
     * null Ausgangsbelege, sondern die Gruppe erscheint gar nicht. «Nichts
     * gefunden» bei `→ sucht in: Eingangsrechnungen` ist eine andere Auskunft
     * als bei allen sechs Arten, und diese Liste ist der Unterschied.
     */
    arten: z.array(suchartSchema),
    /**
     * Die Belegarten, die die **Fähigkeiten des Aufrufers** übriglassen —
     * geschnitten mit `arten` (Spec 16.9.1).
     *
     * **Sie ist die Erlaubnis für die grauen Nullen der Facettenzeile.** Ohne
     * dieses Feld darf die Oberfläche keine Null zeigen: Eine graue `0` an
     * einer Belegart, die der Anwender gar nicht sehen darf, verriete, dass es
     * sie gibt — und genau das verschweigt Spec 7.1.
     *
     * **`arten` ist etwas anderes und wird nie dafür benutzt.** Dort stehen die
     * Arten, die die **Schlüssel** übriglassen (Spec 4.2); das ist eine Aussage
     * über die Frage und keine über die Rechte. Diese Verwechslung wäre der
     * kürzeste Weg, 7.1 zu brechen.
     */
    sichtbareArten: z.array(suchartSchema),
    gruppen: z.array(suchgruppeSchema),
    summe: z.array(suchsummeSchema),
    /** Die Zähler der Zeile `STAND` — siehe {@link suchstandSchema}. */
    staende: z.array(suchstandSchema),
    /**
     * «Ohne … wären es N Treffer» — siehe {@link ohnezeileSchema}. Bei
     * mindestens einem Treffer immer leer.
     */
    ohne: z.array(ohnezeileSchema),
    /** Die Gruppe «Ähnliche Treffer». In Stufe 1 immer `null`. */
    ungefaehr: suchgruppeSchema.nullable(),
    folge: z.number().int().nonnegative(),
    dauerMs: z.number().int().nonnegative(),
})
    .strict();
// ===========================================================================
// Der Vorschlagsendpunkt — `GET /api/v1/suche/vorschlaege`
// ===========================================================================
/**
 * Ein Wertvorschlag: links der Wert, rechts der Kontext.
 *
 * **`wert` ist die Rohfassung und die Anzeige zugleich** — `Müller Bürotechnik
 * GmbH` und nicht `mueller buerotechnik gmbh`. Die Suchform ist der
 * Vergleichsraum und nicht die Anzeige; sie in einen Vorschlag zu schreiben
 * hiesse, sie beim Übernehmen auch in die Eingabe zu schreiben, und der
 * Anwender läse dort einen Namen, den es so nicht gibt.
 *
 * **`kontext` ist ein Satzfetzen und keine Zahl.** «12 Belege», «Eingangsrechnung»,
 * «01.03.2026 – 31.03.2026». Wer daraus rechnen will, rechnet an der falschen
 * Stelle: Diese Angabe ist eine Lesehilfe und niemals eine Auswertung.
 */
export const wertvorschlagSchema = z
    .object({
    wert: z.string(),
    kontext: z.string().nullable(),
})
    .strict();
/**
 * Die Antwort des Vorschlagsendpunkts.
 *
 * **`folge` ist dieselbe Zusage wie bei der Suche** (Spec 8.2): Eine Antwort
 * mit kleinerer Nummer als die zuletzt angezeigte wird verworfen, auch wenn sie
 * ankommt. Der Abbruch über `AbortController` ist ein Wunsch — beim Tippen
 * lösen sich Anfragen im Abstand von Millisekunden ab, und ein spätes Ergebnis
 * darf ein neueres nie überschreiben.
 *
 * **`vollstaendig` sagt, ob die Liste alles ist.** Sie ist auf sieben Einträge
 * gedeckelt (Spec 16.18: höchstens 7 sichtbar, kein Scrollen); ohne diese
 * Angabe läse ein Anwender die siebte Zeile als «und das ist alles».
 */
export const vorschlagsantwortSchema = z
    .object({
    schluessel: suchschluesselSchema,
    /** Der Präfix, auf den geantwortet wurde — unverändert zurückgegeben. */
    praefix: z.string(),
    werte: z.array(wertvorschlagSchema),
    vollstaendig: z.boolean(),
    folge: z.number().int().nonnegative(),
})
    .strict();
/**
 * Wie viele Vorschläge höchstens sichtbar sind — **sieben, ohne Scrollen**.
 *
 * Briefing «Darstellung»: *Höchstens 7 Vorschläge sichtbar, kein Scrollen im
 * Dropdown. Wer scrollen muss, tippt weiter.* Die Zahl steht im Vertrag und
 * nicht in der Maske, weil der Endpunkt sie einhalten muss: Eine Liste, die
 * achtzig Werte schickt und siebzehn davon zeigt, hat achtzig übertragen.
 */
export const VORSCHLAEGE_HOECHSTENS = 7;
/**
 * Die Abfrageparameter der Suche — `GET /api/v1/suche` (`E-1710`).
 *
 * **Hier und nicht im Controller:** Der OpenAPI-Erzeuger löst ein Schema nur
 * auf, wenn es aus `@fameacc/contracts`, aus einem relativ importierten Modul
 * oder als Export der Controller-Datei stammt. Ein nicht exportiertes Schema
 * liess die Route ohne `in: 'query'`-Parameter in `docs/openapi/openapi.json`
 * stehen.
 */
export const sucheAnfrageSchema = z
    .object({
    /** Die Anfrage, roh. */
    q: z.string().max(500).default(''),
    art: suchartSchema.optional(),
    /**
     * Treffer je Gruppe. Vorgabe 5 (das Sprungbrett), Höchstwert 50.
     *
     * **Der Höchstwert ist eine Grenze der Antwortzeit und keine Vorsicht:**
     * Sechs Gruppen zu 50 Treffern sind 300 Zeilen mit Freitext in einem Budget
     * von 200 ms. Wer mehr braucht, will die Liste der Belegart und nicht die
     * Suche.
     */
    limit: z.coerce.number().int().min(1).max(50).default(5),
    /** Die Folgenummer aus Spec 8.2, unverändert zurückgegeben. */
    folge: z.coerce.number().int().min(0).default(0),
})
    .strict();
/**
 * Die Abfrageparameter des Vorschlagsendpunkts — `GET /api/v1/suche/vorschlaege`
 * (`E-1710`, aus demselben Grund wie `sucheAnfrageSchema`).
 *
 * **`schluessel` ist eine Aufzählung und kein Text**, und das ist mehr als
 * Sorgfalt: Die Werteliste verzweigt über ihn auf eine Spalte des Suchindex.
 * Ein freier Text an dieser Stelle wäre die Einladung, den Spaltennamen aus der
 * Anfrage zu nehmen — und genau daraus entsteht eine Auskunft über eine Spalte,
 * die niemand freigegeben hat.
 *
 * **`praefix` darf leer sein.** Der Cursor steht dann direkt hinter
 * `kreditor:`, und die Antwort ist die volle Liste — genau das zweistufige
 * Einfügen des Briefings: Schlüssel übernehmen, Dropdown bleibt offen und zeigt
 * die Werte.
 */
export const vorschlagAnfrageSchema = z
    .object({
    schluessel: suchschluesselSchema,
    praefix: z.string().max(200).default(''),
    folge: z.coerce.number().int().min(0).default(0),
})
    .strict();
//# sourceMappingURL=suche.js.map